import { useAtom } from 'jotai';
import { useState } from 'react';
import { Switch } from '../../../../components';
import { Optional } from '../../../../Common/interfaces';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    Card,
    Exercise,
    FloatingButton,
    GhostButton,
    LabelBody,
    LabelHeader4,
    LabelHeader5,
    ProgressBar,
    SecondaryButton,
    Spacer,
    SwirlyBox,
    Textarea,
} from '../../components';
import { userAtom } from '../../data';
import {
    ExerciseData,
    ExerciseViewProps,
    CustomLogbookViewProps,
} from '../../exercises';
import {
    AddIcon,
    CheckCircleIcon,
    IllustrationHoldingHands,
} from '../../icons';
import { ExerciseDto, ExerciseStatus } from '../../interfaces';
import { useBackgroundColor } from '../../useBackgroundColor';
import {
    normalizeInput,
    parseArray,
    stringifyArray,
    useSyncedState,
} from '../../useSyncedState';
import { RelationsCircle } from '../../views';
import { Relation } from '../../views/RelationsCircle';

const relationsKey = 'relations';
const reflectionKey = 'reflection';

export const exerciseData12: ExerciseData = {
    ExerciseView: Module1Exercise2,
    dataKeys: [relationsKey, reflectionKey],
    questions: [
        createQuestionFromConfiguration({
            dataKeys: [reflectionKey],
            title: 'Refleksion',
            description:
                'Du har nu skabt et overblik over de relationer, du har i dit liv. Når du kigger på din relationscirkel, så kan du spørge dig selv: Hvilke relationer føler jeg mig set og hørt i? Er der nogen relationer, jeg gerne vil have tættere på mig, i denne periode af mit liv?',
        }),
    ],
    LogbookHeader: LogbookRelationCircle,
    haveDataForLogbook(exercise) {
        return parseArray(exercise.Data[relationsKey]).length > 0;
    },
};

function parseRelations(exercise: ExerciseDto) {
    return parseArray(exercise.Data[relationsKey]).map((x) => JSON.parse(x));
}

function LogbookRelationCircle({
    exercise,
    className,
}: CustomLogbookViewProps) {
    return (
        <RelationsCircle
            centerText="Mig"
            relationSize="normal"
            relations={parseRelations(exercise)}
            className={className}
        />
    );
}

function Module1Exercise2({ exercise, onContinue }: ExerciseViewProps) {
    return (
        <Exercise title="Relationscirklen">
            {exercise.CurrentPage !== 3 && exercise.CurrentPage !== 5 && (
                // Page 3 is not represented in the progress bar
                <ProgressBar
                    current={Math.min(exercise.CurrentPage, 3)}
                    total={3}
                />
            )}
            <Switch
                cases={{
                    1: (
                        <Page1
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                });
                            }}
                        />
                    ),
                    3: (
                        <Page3
                            relations={parseRelations(exercise)}
                            onContinue={(relations) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 4,
                                    data: {
                                        [relationsKey]: stringifyArray(
                                            relations.map((x) =>
                                                JSON.stringify(x),
                                            ),
                                        ),
                                    },
                                });
                            }}
                        />
                    ),
                    4: (
                        <Page4
                            relations={parseRelations(exercise)}
                            text={exercise.Data[reflectionKey] ?? ''}
                            onContinue={(text) =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 5,
                                    data: {
                                        [reflectionKey]: normalizeInput(text),
                                    },
                                })
                            }
                        />
                    ),
                    5: (
                        <Page5
                            onContinue={(goToNext) =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext,
                                })
                            }
                        />
                    ),
                }}
                value={exercise.CurrentPage}
                default={1}
            />
        </Exercise>
    );
}

type PageProps = {
    onContinue: () => void;
};
function Page1({ onContinue }: PageProps) {
    return (
        <>
            <LabelHeader4>Hvem er en del af dit liv?</LabelHeader4>
            <LabelBody class="my-10">
                Når vi føler os pressede, er det en helt naturlig reaktion at
                begynde at trække os socialt og opleve, at vi skal klare alting
                selv. Derfor er et sted at starte at fortælle nogen, hvordan du
                har det.
            </LabelBody>
            <LabelBody>
                Vi ved godt, at det kan være svært, fordi du f.eks. ikke vil
                skuffe andre eller at du synes, de andre kan klare det hele så
                hvorfor ikke dig?
            </LabelBody>
            <IllustrationHoldingHands className="mx-auto my-30" />
            <LabelBody>
                I denne øvelse skal du danne et overblik over relationerne i dit
                liv med henblik på at få øje på de relationer, du kan støtte dig
                til i denne periode. Det er vigtigt, at du ikke står alene med
                dét, der presser dig. Husk på at du bare er et menneske, ligesom
                alle andre, og at ingen mennesker kan stå alene i at have det
                svært.
            </LabelBody>
            <SecondaryButton class="mt-30" onClick={onContinue}>
                Gå i gang
            </SecondaryButton>
        </>
    );
}

function Page2({ onContinue }: PageProps) {
    return (
        <>
            <LabelHeader4>Kortlæg dine relationer</LabelHeader4>
            <LabelBody class="mt-10">
                Placer dem du føler dig tættest med eller er betydningsfulde for
                dig i indercirklen. Dem, du er mindre tætte med, placerer du
                længere væk fra midten. Du kan rykke relationerne frem og
                tilbage.
            </LabelBody>
            <RelationsCircle
                className="my-30"
                centerText="Dig selv"
                relations={[
                    { text: 'Bedste ven', x: -13, y: -13 },
                    { text: 'Venner', x: 53, y: -26 },
                    { text: 'Kollega', x: -83, y: -17 },
                    { text: 'Forældre', x: -23, y: 10 },
                    { text: 'Søster', x: 33, y: 0 },
                    { text: 'Bedsteforældre', x: -26, y: 73 },
                ]}
                relationSize="small"
            />
            <SwirlyBox>Er du klar til at gå i gang?</SwirlyBox>
            <Spacer class="mt-30" />
            <SecondaryButton onClick={onContinue}>
                Udfyld din relationscirkel
            </SecondaryButton>
        </>
    );
}

type Page3Props = {
    onContinue: (relations: Relation[]) => void;
    relations: Relation[];
};
function Page3({ relations: r, onContinue }: Page3Props) {
    useBackgroundColor('bg-interactive-surface');
    const [user] = useAtom(userAtom);
    const [relations, setRelations] = useSyncedState(r);
    const [newRelation, setNewRelation] = useState<Optional<string>>(null);

    return (
        <div className="flex grow flex-col">
            <RelationsCircle
                className="my-20"
                centerText="Mig"
                relations={relations}
                onChangeRelations={setRelations}
                relationSize="normal"
                customRelation={
                    newRelation == null
                        ? null
                        : {
                              x: 0,
                              y: 85,
                              element: (
                                  <form
                                      onSubmit={(e) => {
                                          e.preventDefault();
                                          addNewRelation();
                                      }}
                                  >
                                      <input
                                          type="text"
                                          className="text-center text-body outline-none"
                                          autoFocus
                                          onBlur={() => addNewRelation()}
                                          value={newRelation}
                                          onChange={(e) => {
                                              setNewRelation(
                                                  e.currentTarget.value,
                                              );
                                              e.currentTarget.style.width =
                                                  e.currentTarget.scrollWidth +
                                                  'px';
                                          }}
                                          style={{ width: '2em' }}
                                      />
                                  </form>
                              ),
                          }
                }
            />
            {newRelation == null && (
                <>
                    <Spacer />
                    <div className="mx-20 mb-40 text-center">
                        <FloatingButton
                            icon={<AddIcon />}
                            text="Tilføj"
                            onClick={() => setNewRelation('')}
                        />
                        <FloatingButton
                            className="ml-20"
                            icon={<CheckCircleIcon />}
                            disabled={relations.length === 0}
                            text="Færdig"
                            onClick={() => onContinue(relations)}
                        />
                    </div>
                </>
            )}
        </div>
    );

    function addNewRelation() {
        if (normalizeInput(newRelation)) {
            setRelations(
                relations.concat({
                    text: newRelation,
                    x: 0,
                    y: 85,
                }),
            );
        }
        setNewRelation(null);
    }
}

type Page4Props = {
    relations: Relation[];
    text: string;
    onContinue: (text: string) => void;
};
function Page4({ relations, text: t, onContinue }: Page4Props) {
    const [text, setText] = useSyncedState(t);
    return (
        <>
            <Card>
                <RelationsCircle
                    centerText="Mig"
                    relations={relations}
                    relationSize="normal"
                    className="mt-20"
                />
                <LabelHeader5 className="mb-10 mt-20">Refleksion</LabelHeader5>
                <LabelBody>
                    Du har nu skabt et overblik over de relationer, du har i dit
                    liv. Når du kigger på din relationscirkel, så kan du spørge
                    dig selv: Hvilke relationer føler jeg mig set og hørt i? Er
                    der nogen relationer, jeg gerne vil have tættere på mig i
                    denne periode af mit liv?
                </LabelBody>
                <Textarea
                    className="my-20"
                    placeholder="F.eks. Jeg vil gerne have mine forældre tættere på."
                    text={text}
                    onChangeText={setText}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer className="mt-30" />
            <SecondaryButton onClick={() => onContinue(text)}>
                Fortsæt
            </SecondaryButton>
        </>
    );
}

type Page5Props = {
    onContinue: (goToNext: boolean) => void;
};
function Page5({ onContinue }: Page5Props) {
    return (
        <>
            <LabelHeader4>Afrunding</LabelHeader4>
            <LabelBody className="my-10">
                Vi håber nu, at du har fået en klarere fornemmelse af, hvilke
                relationer der er vigtige i dit liv.
            </LabelBody>
            <LabelBody>
                Vi har ikke kontrol over vores relationer, men vi har en
                indflydelse på, hvem vi rækker ud til eller betror os til. Det
                kan derfor være en hjælp i denne periode at prioritere
                relationer, hvor du føler dig set og hjulpet. Det er nemlig dig,
                der står i midten af din relationscirkel, og derfor også dig der
                bedst ved, hvordan du kan søge hjælp og hos hvem.
            </LabelBody>
            <LabelBody className="mb-30 mt-10">
                Der er sikkert mange, der gerne vil hjælpe, og det bliver
                nemmere for dem, hvis du rækker ud og fortæller om, hvordan du
                har det. Det vil næste øvelse hjælpe dig med.
            </LabelBody>
            <SwirlyBox>Er du klar til at bede om hjælp?</SwirlyBox>
            <Spacer className="mt-30" />
            <SecondaryButton className="mb-20" onClick={() => onContinue(true)}>
                Jeg er klar
            </SecondaryButton>
            <GhostButton onClick={() => onContinue(false)}>
                Vender tilbage senere
            </GhostButton>
        </>
    );
}
